import { Text, Flex, CheckboxGroup, Switch, Button, useToast, InputGroup, SimpleGrid } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import {
  useAccessControlUserById,
  useAccessControlPermissionTypes,
  useUpdateAccessControlUserMutation,
} from '../../services/queries/access-control';
import { accessControlUserEditBreadcrumb } from './breadcrumb';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { accessControlUserEditSchema, AccessControlUserEditFormData } from './schemas';
import { Permission } from '@/model';
import { useEffect } from 'react';
import { SelectField } from '@/components/SelectField';
import { formatDocument } from '@/utils/formatDocument';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { formatPhoneNumber } from '@/utils';
import { AccessControlUserStatus, PermissionType } from '@/model/access-control';
import { queryClient } from '@/services/api/queryClient';
import { useTranslation } from 'react-i18next';

export const AccessControlUserEdit = () => {
  const { id } = useParams();
  const toast = useToast();
  const { mutate: updateAccessControlUser, isLoading: isLoadingCreateAccessControlPermissionType } =
    useUpdateAccessControlUserMutation();
  const { data: accessControlUser } = useAccessControlUserById(id as string);
  const { t } = useTranslation();

  const { data: permissionTypesData } = useAccessControlPermissionTypes();

  // POR ALGUM MOTIVO ESSE MOCK NÃO TÁ FUNCIONANDO CERTO, ELE NÃO RETORNA O OBJETO NO FORMATO { data: [], meta: {} }
  // RETORNA O ARRAY DIRETO. PRA NÃO PERDER TEMPO FIZ ESSE WORKAROUND PRA VERIFICAR SE O SUBSCRIPTION É ARRAY (PQ NO MOCK, ELE É ARRAY)
  const permissionTypes = Array.isArray(permissionTypesData)
    ? (permissionTypesData as unknown as PermissionType[])
    : permissionTypesData?.data || [];

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AccessControlUserEditFormData>({
    defaultValues: {
      blocked: false,
      permission_type_id: '',
      permissions: {
        ACCESS_CONTROL: false,
        ACCESS_PLANS: false,
        APPLICATION: false,
        COMMUNICATION_CENTRAL: false,
        FINANCIAL: false,
        HOME: false,
        PARTNERS: false,
        SUBSCRIBERS: false,
        USERS: false,
      },
    },
    resolver: zodResolver(accessControlUserEditSchema),
  });

  const permissionsValues = watch('permissions');
  const isUserBlocked = watch('blocked');

  const handlePermissionChange = (permission: Permission, value: boolean) => {
    setValue(`permissions.${permission}`, value as never);
  };

  const onSubmit = (data: AccessControlUserEditFormData) => {
    updateAccessControlUser(
      {
        id: id as string,
        payload: {
          ...data,
          status: data.blocked ? AccessControlUserStatus.BLOCKED : AccessControlUserStatus.ACTIVE,
        },
      },
      {
        onSuccess: async () => {
          toast({
            title: t('pages.access_control_user_edit.success_toast'),
            status: 'success',
          });

          await queryClient.invalidateQueries(['GET_ACCESS_CONTROL_USERS']);
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.access_control_user_edit.error_toast'),
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!accessControlUser) return;

    setValue('blocked', accessControlUser.status === AccessControlUserStatus.BLOCKED);
    setValue('permission_type_id', accessControlUser.permission_type.id);
    setValue('permissions', accessControlUser.permission_type.permissions);
  }, [accessControlUser, setValue]);

  return (
    <PageContent title={t('pages.access_control_user_edit.title')} breadcrumbItems={accessControlUserEditBreadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction='column' gap='deka'>
          <Flex as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={8}>
            <Flex direction='column' gap={4} alignItems='flex-start'>
              <Text fontWeight='medium' fontSize='18px' mb={2}>
                {t('pages.access_control_user_edit.section_user_data')}
              </Text>

              <Flex w='100%' justifyContent='space-between' gap={4} direction={{ base: 'column', md: 'row' }}>
                <Flex w='100%' gap={8} direction={{ base: 'column', md: 'row' }}>
                  <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} columnGap={8} rowGap={4}>
                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '50px' }}> {t('pages.access_control_user_edit.name')}</Text>
                      <Text fontWeight='medium'>{accessControlUser?.name}</Text>
                    </Flex>

                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '70px' }}> {t('pages.access_control_user_edit.email')}</Text>
                      <Text fontWeight='medium'>{accessControlUser?.email}</Text>
                    </Flex>

                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '100px' }}>{t('pages.access_control_user_edit.creation_date')}</Text>
                      <Text fontWeight='medium'>
                        {accessControlUser?.created_at ? formatDateToDateTimeDisplay(accessControlUser?.created_at) : '-'}
                      </Text>
                    </Flex>

                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '50px' }}>{t('pages.access_control_user_edit.document')}</Text>
                      <Text fontWeight='medium'>
                        {accessControlUser?.document ? formatDocument(accessControlUser?.document) : '-'}
                      </Text>
                    </Flex>

                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '70px' }}>{t('pages.access_control_user_edit.phone')}</Text>
                      <Text fontWeight='medium'>
                        {accessControlUser?.phone_number ? formatPhoneNumber(accessControlUser?.phone_number) : '-'}
                      </Text>
                    </Flex>

                    <Flex gap={2} direction={{ base: 'column', xl: 'row' }}>
                      <Text minW={{ xl: '100px' }}>{t('pages.access_control_user_edit.last_access')}</Text>
                      <Text fontWeight='medium'>
                        {accessControlUser?.last_access ? formatDateToDateTimeDisplay(accessControlUser?.last_access) : '-'}
                      </Text>
                    </Flex>
                  </SimpleGrid>

                  <InputGroup display='flex' flexDirection='column' maxW={{ base: 'unset', sm: '200px' }}>
                    <Text fontSize='14px' fontWeight='medium'>
                      {t('pages.access_control_user_edit.type_label')}
                    </Text>
                    <SelectField
                      {...register('permission_type_id')}
                      rootProps={{
                        isInvalid: !!errors?.permission_type_id,
                        errorMessage: errors?.permission_type_id?.message?.toString(),
                        mt: 0,
                      }}
                    >
                      <option value='' disabled>
                        {t('pages.access_control_user_edit.placeholder')}
                      </option>
                      {permissionTypes?.map(permissionType => (
                        <option value={permissionType.id}>{permissionType.name}</option>
                      ))}
                    </SelectField>
                  </InputGroup>
                </Flex>

                <Button minW='fit-content' type='submit' display={{ base: 'none', md: 'initial' }}>
                  {t('pages.access_control_user_edit.save_changes')}
                </Button>
              </Flex>
            </Flex>

            <Flex direction='column' gap={4} alignItems='flex-start'>
              <Text fontWeight='medium' fontSize='18px' mt={6} mb={2}>
                {t('pages.access_control_user_edit.section_user_status')}
              </Text>

              <Switch isChecked={isUserBlocked} onChange={event => setValue('blocked', event.target.checked)}>
                {t('pages.access_control_user_edit.blocked')}
              </Switch>
            </Flex>

            <Flex direction='column' gap={4} alignItems='flex-start'>
              <Text fontWeight='medium' fontSize='18px' mt={6} mb={2}>
                {t('pages.access_control_user_edit.section_user_permissions')}
              </Text>
              <CheckboxGroup>
                <Switch value={Permission.HOME} onChange={event => handlePermissionChange(Permission.HOME, event.target.checked)}>
                  {t('pages.access_control_user_edit.home')}
                </Switch>
                <Switch
                  value={Permission.USERS}
                  isChecked={permissionsValues.USERS}
                  onChange={event => handlePermissionChange(Permission.USERS, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.users')}
                </Switch>
                <Switch
                  value={Permission.SUBSCRIBERS}
                  isChecked={permissionsValues.SUBSCRIBERS}
                  onChange={event => handlePermissionChange(Permission.SUBSCRIBERS, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.subscribers')}
                </Switch>
                <Switch
                  value={Permission.ACCESS_PLANS}
                  isChecked={permissionsValues.ACCESS_PLANS}
                  onChange={event => handlePermissionChange(Permission.ACCESS_PLANS, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.access_plans')}
                </Switch>
                <Switch
                  value={Permission.APPLICATION}
                  isChecked={permissionsValues.APPLICATION}
                  onChange={event => handlePermissionChange(Permission.APPLICATION, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.application')}
                </Switch>
                <Switch
                  value={Permission.FINANCIAL}
                  isChecked={permissionsValues.FINANCIAL}
                  onChange={event => handlePermissionChange(Permission.FINANCIAL, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.financial')}
                </Switch>
                <Switch
                  value={Permission.PARTNERS}
                  isChecked={permissionsValues.PARTNERS}
                  onChange={event => handlePermissionChange(Permission.PARTNERS, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.partners')}
                </Switch>
                <Switch
                  value={Permission.ACCESS_CONTROL}
                  isChecked={permissionsValues.ACCESS_CONTROL}
                  onChange={event => handlePermissionChange(Permission.ACCESS_CONTROL, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.access_control')}
                </Switch>
                <Switch
                  value={Permission.COMMUNICATION_CENTRAL}
                  isChecked={permissionsValues.COMMUNICATION_CENTRAL}
                  onChange={event => handlePermissionChange(Permission.COMMUNICATION_CENTRAL, event.target.checked)}
                >
                  {t('pages.access_control_user_edit.communication_central')}
                </Switch>
              </CheckboxGroup>
              {errors?.permissions ? <Text color='red.500'>{errors?.permissions?.message}</Text> : null}
            </Flex>

            <Button type='submit' display={{ md: 'none' }}>
              {t('pages.access_control_user_edit.save_changes')}
            </Button>
          </Flex>
        </Flex>
      </SectionBox>

      <CenteredSpinner isLoading={isLoadingCreateAccessControlPermissionType} />
    </PageContent>
  );
};
